import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';

import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export enum ConfirmMessageType {
  Default = 'default',
  Bulleted = 'bulleted',
}

/**
 * Represents the data structure for the confirm dialog.
 */
export interface ConfirmDialogData {
  /**
   * The title of the confirm dialog.
   */
  title: string;

  /**
   * An optional array of messages to display in the confirm dialog.
   */
  messages?: string[];

  /**
   * Specifies whether the messages should be displayed as bulleted list.
   */
  messageType?: ConfirmMessageType;
  /**
   * The text to display on the confirm button.
   */
  confirmText?: string;

  /**
   * The value associated with the confirm button.
   */
  confirmValue?: boolean;

  /**
   * The text to display on the cancel button.
   */
  cancelText?: string;

  /**
   * The value associated with the cancel button.
   */
  cancelValue?: boolean;
}

/**
 * Represents a confirm dialog component.
 */
@Component({
  selector: 'cbre-confirm',
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="dialog confirm-dialog">
      <div mat-dialog-title>
        <h1>{{ title }}</h1>
      </div>
    
      <div mat-dialog-content class="confirm-container">
        <div class="viewmode">
          <mat-icon><em class="fa fa-exclamation-triangle"></em></mat-icon>
          <div class="alert-msgs">
            <!-- CONFIRM MESSAGES -->
            @if (data.messageType === 'bulleted') {
              <!-- BULLETED MULTI MESSAGE -->
              <ul class="bulleted-multi-message">
                @for (message of data.messages; track message) {
                  <li>{{ message }}</li>
                }
              </ul>
            } @else {
              @for (message of data.messages; track message) {
                <p class="message">{{ message }}</p>
              }
            }
            <!-- DEFAULT MULTI MESSAGE -->
          </div>
        </div>
    
        <div mat-dialog-actions>
          @if (data.cancelText) {
            <button (click)="close(cancelValue)" class="btn button button--outlined">
              {{ data.cancelText }}
            </button>
          }
          <ng-container>
            <button (click)="close(confirmValue)" class="button btn btn-submit" cdkFocusInitial>
              {{ confirmText }}
            </button>
          </ng-container>
        </div>
      </div>
    </div>
    `,
  styleUrl: './confirm-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmDialogComponent {
  /**
   * The title of the confirm dialog.
   */
  title: string = 'Confirm';

  /**
   * The text to display on the confirm button.
   */
  confirmText: string = 'Ok';

  /**
   * The value associated with the confirm button.
   */
  confirmValue: any = true;

  /**
   * The value associated with the cancel button.
   */
  cancelValue: any = null;

  /**
   * Creates an instance of ConfirmDialogComponent.
   * @param dialogRef - The reference to the dialog.
   * @param data - The data for the confirm dialog.
   */
  constructor(
    public dialogRef: MatDialogRef<ConfirmDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmDialogData
  ) {
    this._setData(data);
  }

  /**
   * Sets the data for the confirm dialog.
   * @param data - The data for the confirm dialog.
   * @private
   */
  private _setData(data: ConfirmDialogData) {
    this.title = data.title ?? this.title;
    this.confirmText = data.confirmText ?? this.confirmText;
    this.confirmValue = data.confirmValue ?? true;
    this.cancelValue = data.cancelValue ?? false;
  }

  /**
   * Selects an option and closes the dialog.
   * @param value - The value associated with the selected option.
   */
  public close(value: any) {
    this.dialogRef.close(value);
  }
}
